import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { reportsData } from '../data/reports';
import ReportShareModal from '../components/ReportShareModal';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../data/permissions';

const { FiArrowLeft, FiPrinter, FiDownload, FiCheckCircle, FiAlertTriangle, FiInfo, FiTrendingUp, FiSettings, FiShare2 } = FiIcons;

const ReportView = () => {
  const { id } = useParams();
  const { can } = usePermissions();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const report = reportsData[id];

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Report not found</h2>
          <Link to="/dashboard" className="text-blue-600 hover:underline flex items-center justify-center">
            <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Strong': case 'Exceptional': case 'Healthy': return <SafeIcon icon={FiCheckCircle} className="text-green-500" />;
      case 'Stable': return <SafeIcon icon={FiInfo} className="text-blue-500" />;
      default: return <SafeIcon icon={FiAlertTriangle} className="text-yellow-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 no-print">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="text-gray-600 hover:text-blue-600 flex items-center transition-colors">
            <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Dashboard
          </Link>
          <div className="flex gap-2">
            {can(PERMISSIONS.SHARE_REPORTS) && (
              <button 
                onClick={() => setIsShareModalOpen(true)}
                className="px-4 py-2 border border-blue-200 bg-blue-50 rounded-lg text-sm font-bold text-blue-700 hover:bg-blue-100 flex items-center transition-colors"
              >
                <SafeIcon icon={FiShare2} className="mr-2" /> Share
              </button>
            )}
            {can(PERMISSIONS.CUSTOMIZE_REPORT) && (
              <Link to={`/report/${id}/customize`} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center transition-colors">
                <SafeIcon icon={FiSettings} className="mr-2" /> Customize
              </Link>
            )}
            <button onClick={handlePrint} className="px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 flex items-center transition-colors shadow-sm">
              <SafeIcon icon={FiDownload} className="mr-2" /> PDF
            </button>
          </div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200 print:shadow-none print:border-none">
          <div className="bg-slate-900 text-white p-8 md:p-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center font-bold text-lg">BA</div>
                  <span className="text-sm font-semibold tracking-wider uppercase text-slate-400">Business 360° Report</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{report.assessmentTitle}</h1>
                <p className="text-slate-400">Completed on {report.completedDate}</p>
              </div>
              <div className="text-center bg-white/10 p-6 rounded-2xl backdrop-blur-sm">
                <div className={`text-5xl font-bold ${getScoreColor(report.overallScore)}`}>
                  {report.overallScore}<span className="text-2xl text-slate-400">%</span>
                </div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 mt-1">Overall Score</div>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-12 border-b border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="text-blue-600 mr-2" /> Executive Summary
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed italic">
              "{report.summary}"
            </p>
          </div>

          <div className="p-8 md:p-12 space-y-12">
            {report.sections.map((section, index) => (
              <div key={index} className="group">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-8 bg-blue-600 rounded-full"></div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusIcon(section.status)}
                        <span className="text-sm font-medium text-gray-500">{section.status}</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-100">
                    <span className={`text-2xl font-bold ${getScoreColor(section.score)}`}>{section.score}%</span>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100/50">
                    <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-3">Assessment Findings</h4>
                    <p className="text-gray-700 leading-relaxed">{section.feedback}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Key Recommendations</h4>
                    <ul className="space-y-3">
                      {section.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="mt-1.5 w-1.5 h-1.5 bg-blue-600 rounded-full flex-shrink-0"></div>
                          <span className="text-gray-600 text-sm">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gray-50 p-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="text-sm text-gray-500">
                © 2024 Cansulta Business 360. All rights reserved.
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Powered by</span>
                <span className="font-bold text-gray-700">Cansulta</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      <ReportShareModal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} reportTitle={report.assessmentTitle} />
    </div>
  );
};

export default ReportView;